/**
 * The app navigator (formerly "AppNavigator" and "MainNavigator") is used for the primary
 * navigation flows of your app.
 * Generally speaking, it will contain an auth flow (registration, login, forgot password)
 * and a "main" flow which the user will use once logged in.
 */
import { ComponentProps, lazy, Suspense } from "react"
import { NavigationContainer } from "@react-navigation/native"
import { createNativeStackNavigator, NativeStackScreenProps } from "@react-navigation/native-stack"

import Config from "@/config"
import { EntryScreen } from "@/screens/EntryScreen"
import { ErrorBoundary } from "@/screens/ErrorScreen/ErrorBoundary"
import { FleetHubLoginScreen } from "@/screens/FleetHubLoginScreen"
import { FleetHubScreen } from "@/screens/FleetHubScreen"
import { OrderTrackingScreen } from "@/screens/OrderTrackingScreen"
import { useAppTheme } from "@/theme/context"

import type { FleetHubReturnRoute } from "./fleetHubReturnTo"
import { navigationRef, useBackButtonHandler } from "./navigationUtilities"

/**
 * PSP view (PLA-861), lazy so the regular FleetHub bundle doesn't carry it.
 * Platform resolution picks `PspOverviewScreen.web.tsx` on web.
 */
const LazyPspOverviewScreen = lazy(() =>
  import("@/screens/psp/PspOverviewScreen").then((m) => ({ default: m.PspOverviewScreen })),
)

function FleetHubOverviewRoute() {
  return (
    <Suspense fallback={null}>
      <LazyPspOverviewScreen />
    </Suspense>
  )
}

/**
 * This type allows TypeScript to know what routes are defined in this navigator
 * as well as what properties (if any) they might take when navigating to them.
 *
 * For more information, see this documentation:
 *   https://reactnavigation.org/docs/params/
 *   https://reactnavigation.org/docs/typescript#type-checking-the-navigator
 *   https://reactnavigation.org/docs/typescript/#organizing-types
 */
export type AppStackParamList = {
  Entry: undefined
  OrderTracking: { orderId?: string; trackingToken?: string } | { token: string }
  OrderTrackingSuccess: { token: string }
  FleetHubLogin: { reason?: "b2b-access-revoked"; returnTo?: FleetHubReturnRoute } | undefined
  FleetHub: undefined
  /** PSP view (PLA-856), `/fleethub/overview`. */
  FleetHubOverview: undefined
  // 🔥 Your screens go here
  // IGNITE_GENERATOR_ANCHOR_APP_STACK_PARAM_LIST
}

/**
 * This is a list of all the route names that will exit the app if the back button
 * is pressed while in that screen. Only affects Android.
 */
const exitRoutes = Config.exitRoutes

export type AppStackScreenProps<T extends keyof AppStackParamList> = NativeStackScreenProps<
  AppStackParamList,
  T
>

// Documentation: https://reactnavigation.org/docs/stack-navigator/
const Stack = createNativeStackNavigator<AppStackParamList>()

const AppStack = () => {
  const {
    theme: { colors },
  } = useAppTheme()

  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        navigationBarColor: colors.background,
        contentStyle: {
          backgroundColor: colors.background,
        },
      }}
      initialRouteName="Entry"
    >
      {/** 🔥 Your screens go here */}
      <Stack.Screen
        name="Entry"
        component={EntryScreen}
        options={{
          title: "HolDeinePlakette",
        }}
      />
      <Stack.Screen
        name="OrderTracking"
        component={OrderTrackingScreen}
        options={{
          title: "HolDeinePlakette Tracking",
        }}
      />
      <Stack.Screen
        // @ts-expect-error - OrderTrackingSuccess is in the types but TS can't infer correctly
        name="OrderTrackingSuccess"
        component={OrderTrackingScreen}
        options={{
          title: "HolDeinePlakette Tracking",
        }}
      />
      <Stack.Screen
        name="FleetHubLogin"
        component={FleetHubLoginScreen}
        options={{
          title: "FleetHub Login",
        }}
      />
      <Stack.Screen
        name="FleetHub"
        component={FleetHubScreen}
        options={{
          title: "FleetHub Dashboard",
        }}
      />
      <Stack.Screen
        name="FleetHubOverview"
        component={FleetHubOverviewRoute}
        options={{
          title: "FleetHub Übersicht",
        }}
      />
      {/* IGNITE_GENERATOR_ANCHOR_APP_STACK_SCREENS */}
    </Stack.Navigator>
  )
}

export interface NavigationProps
  extends Partial<ComponentProps<typeof NavigationContainer<AppStackParamList>>> {}

export const AppNavigator = (props: NavigationProps) => {
  const { navigationTheme } = useAppTheme()

  useBackButtonHandler((routeName) => exitRoutes.includes(routeName))

  return (
    <NavigationContainer ref={navigationRef} theme={navigationTheme} {...props}>
      <ErrorBoundary catchErrors={Config.catchErrors}>
        <AppStack />
      </ErrorBoundary>
    </NavigationContainer>
  )
}
